import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'hooks/apollo';
import { gql } from '@apollo/client';
import useDataSource from 'hooks/useDataSource';
import Loading from 'components/Loading';
import globalMessages from 'translations/messages';
import EmptyMessage from 'components/EmptyMessage';
import { useLocalStorageState } from 'ahooks';
import queryString from 'query-string';
import { EDIT_TAG_MANAGER } from 'shared/constants/permissions';
import messages from '../messages';

const QUERY = gql`
  query ($websiteId: ID!, $permission: String) {
    user {
      id
      matomoLoginUrl(websiteId: $websiteId, permission: $permission)
    }
    config {
      matomo {
        baseUrl
      }
    }
  }
`;

const extraFields = `
  website {
    id
    matomoId
  }
`;

const baseParams = {
  module: 'CoreHome',
  action: 'index',
  period: 'range',
  date: 'last30',
};

const EmbedMatomo = ({ urlParams, permission, disableGlobalParams }) => {
  const { dataSource, loading: dataSourceLoading, getDataSourceLicenseSetting, hasDataSourcePermission } = useDataSource({ extraFields });
  const hasPermission = !permission || hasDataSourcePermission(permission);
  const { data, loading } = useQuery(QUERY, { variables: { websiteId: dataSource?.website?.id, permission }, skip: !dataSource?.website?.id });
  const [loginLoading, setLoginLoading] = useState(true);
  const [urlLoading, setUrlLoading] = useState(true);
  const [url, setUrl] = useState();
  const [storedParams, setStoredParams] = useLocalStorageState(`matomoParams-${dataSource?.website?.matomoId}`, { period: 'range', date: 'last30' });

  useEffect(() => {
    if (!loginLoading && data?.config?.matomo?.baseUrl && dataSource?.website?.matomoId) {
      const newUrl = queryString.stringifyUrl({
        url: data?.config.matomo.baseUrl,
        query: {
          ...(disableGlobalParams ? {} : baseParams),
          ...(disableGlobalParams ? {} : storedParams),
          idSite: dataSource?.website?.matomoId,
          ...urlParams,
        },
      }, {
        arrayFormat: 'bracket',
        encode: false,
      });

      if (newUrl !== url) {
        setUrl(newUrl);
        setUrlLoading(true);
      }
    }
  }, [data, dataSource, storedParams, urlParams, loginLoading, url, disableGlobalParams]);

  const onMessage = useCallback((e) => {
    const { type, payload } = e.data;
    if (type === 'matomoHashChanged') {
      const params = queryString.parse(payload, { arrayFormat: 'bracket', decode: false });
      if (params.period && params.date) {
        setStoredParams({
          date: params.date,
          period: params.period,
          comparePeriods: params.comparePeriods,
          comparePeriodType: params.comparePeriodType,
          compareDates: params.compareDates,
          segment: params.segment,
          compareSegments: params.compareSegments,
        });
      }
    }
    if (type === 'matomoLoginRequired') {
      setLoginLoading(true);
    }
  });

  useEffect(() => {
    if (!disableGlobalParams) {
      window.addEventListener('message', onMessage);
    }
    return () => {
      if (!disableGlobalParams) {
        window.removeEventListener('message', onMessage);
      }
    };
  }, [onMessage, disableGlobalParams]);

  if (loading || dataSourceLoading) {
    return <Loading />;
  }

  if (!dataSource?.website?.matomoId) {
    return (
      <EmptyMessage message={globalMessages.selectWebsite} />
    );
  }

  if (permission === EDIT_TAG_MANAGER && !hasPermission) {
    return (
      <EmptyMessage message={messages.noTagManager} />
    );
  }

  if (!data?.user?.matomoLoginUrl) {
    const limit = getDataSourceLicenseSetting('matomoSegmentLimit');

    if (limit === 0) {
      return (
        <EmptyMessage message={messages.noMatomoLicense} />
      );
    }

    return (
      <EmptyMessage message={messages.noMatomoTry} />
    );
  }

  return (
    <>
      {loginLoading && <iframe className="fullsize" title="matomo-login" src={data.user.matomoLoginUrl} style={{ visibility: 'hidden' }} onLoad={() => setLoginLoading(false)} />}
      {url && <iframe className="fullsize" title="matomo" src={url} onLoad={() => setUrlLoading(false)} />}
      {urlLoading && <Loading cover />}
    </>
  );
};

export default EmbedMatomo;
